import { createRef, useRef, useEffect, useState, UIEvent } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Spinner } from 'components/UI';

export type InfiniteScrollProps = {
  hasMore?: boolean;
  onLoadMore: () => Promise<void>;
  scrollTop?: number | null;
  loading?: boolean;
  maxHeight?: number;
  children?: React.ReactNode;
};

const InfiniteScroll = ({
  hasMore = false,
  onLoadMore,
  scrollTop = null,
  loading: propsLoading,
  maxHeight = 300,
  children,
}: InfiniteScrollProps) => {
  const [loading, setLoading] = useState(false);
  const infiniteScrollRef = useRef() as React.LegacyRef<HTMLDivElement>;

  const ref = createRef<HTMLDivElement>();

  useEffect(() => {
    // Restore the scrollbar's scroll if it has to remount from a scroll
    if (ref.current && scrollTop) {
      ref.current.scrollTop = scrollTop;
    }
  }, []);

  const handleLoadMore = async () => {
    setLoading(true);

    await onLoadMore();

    setLoading(false);
  };

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const isScrolled = target.scrollTop + target.clientHeight >= target.scrollHeight - 60;

    if (isScrolled && hasMore && !loading) {
      handleLoadMore();
    }
  };

  const isLoading = propsLoading || loading;

  const renderLoader = isLoading ? (
    <Flex justifyContent="center" mt={1}>
      <Spinner />
    </Flex>
  ) : null;

  return (
    <Box
      maxH={maxHeight}
      overflowY="auto"
      ref={infiniteScrollRef}
      onScroll={handleScroll}
      sx={{
        '&::-webkit-scrollbar': {
          w: 2.5,
        },
        '&::-webkit-scrollbar-thumb': {
          bgColor: 'scrollbarThumbColour.400',
        },
      }}
      data-testid="infinite-scroll"
    >
      {children}
      {renderLoader}
    </Box>
  );
};

export default InfiniteScroll;
